import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CreditCard,
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  Star,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { billingApi } from "@/services/api";

const CARD_BRANDS = {
  visa: "💳 Visa",
  mastercard: "💳 Mastercard",
  amex: "💳 American Express",
  discover: "💳 Discover",
  diners: "💳 Diners Club",
  jcb: "💳 JCB",
  unionpay: "💳 UnionPay",
  unknown: "💳 Card",
};

export const PaymentMethods = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    method: null,
  });
  const [processingId, setProcessingId] = useState(null);

  // ============================================
  // TanStack Query - Fetch Payment Methods
  // ============================================
  const { data: methods = [], isLoading: loading } = useQuery({
    queryKey: ["payment-methods"],
    queryFn: async () => {
      const response = await billingApi.getPaymentMethods();
      return response.data.data || [];
    },
    meta: {
      onError: () => {
        toast.error("Failed to load payment methods");
      },
    },
  });

  // ============================================
  // TanStack Query - Mutations
  // ============================================
  const addCardMutation = useMutation({
    mutationFn: () => billingApi.createSetupIntent(),
    onSuccess: () => {
      toast.success("Redirecting to add payment method...");
      // The actual implementation would use Stripe Elements here
    },
    onError: () => {
      toast.error("Failed to add payment method");
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (methodId) => {
      setProcessingId(methodId);
      return billingApi.setDefaultPaymentMethod(methodId);
    },
    onSuccess: () => {
      toast.success("Default payment method updated");
      queryClient.invalidateQueries({ queryKey: ["payment-methods"] });
    },
    onError: () => {
      toast.error("Failed to update default payment method");
    },
    onSettled: () => {
      setProcessingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (methodId) => {
      setProcessingId(methodId);
      return billingApi.deletePaymentMethod(methodId);
    },
    onSuccess: () => {
      toast.success("Payment method removed");
      setDeleteDialog({ open: false });
      queryClient.invalidateQueries({ queryKey: ["payment-methods"] });
    },
    onError: () => {
      toast.error("Failed to remove payment method");
    },
    onSettled: () => {
      setProcessingId(null);
    },
  });

  const addingCard = addCardMutation.isPending;

  const handleAddCard = () => {
    addCardMutation.mutate();
  };

  const handleSetDefault = (methodId) => {
    setDefaultMutation.mutate(methodId);
  };

  const handleDelete = (methodId) => {
    deleteMutation.mutate(methodId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            {t("billing.paymentMethods.title")}
          </h2>
          <p className="text-muted-foreground">
            {t("billing.paymentMethods.desc")}
          </p>
        </div>
        <Button
          onClick={handleAddCard}
          disabled={addingCard}
          size={isMobile ? "mobile" : "default"}
          className={isMobile ? "w-full" : ""}
        >
          {addingCard ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Plus className="h-4 w-4 mr-2" />
          )}
          Add Payment Method
        </Button>
      </div>

      {/* Payment Methods List */}
      {methods.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CreditCard className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {t("billing.noPaymentMethods")}
            </h3>
            <p className="text-muted-foreground mb-4">
              Add a payment method to subscribe to plans and purchase credits.
            </p>
            <Button onClick={handleAddCard}>
              <Plus className="h-4 w-4 mr-2" />
              Add Payment Method
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {methods.map((method) => (
            <Card
              key={method.id}
              className={method.isDefault ? "border-primary" : ""}
            >
              <CardContent className="flex items-center justify-between py-4">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-muted rounded-lg">
                    <CreditCard className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {CARD_BRANDS[method.brand] || CARD_BRANDS.unknown} ••••{" "}
                        {method.last4}
                      </span>
                      {method.isDefault && (
                        <Badge variant="secondary">
                          <Star className="h-3 w-3 mr-1" />
                          Default
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Expires {method.expMonth}/{method.expYear}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!method.isDefault && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSetDefault(method.id)}
                      disabled={processingId === method.id}
                    >
                      {processingId === method.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Set as Default"
                      )}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteDialog({ open: true, method })}
                    disabled={processingId === method.id}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Security Note */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Your payment information is securely stored by Stripe. We never have
          access to your full card details.
        </AlertDescription>
      </Alert>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("billing.paymentMethods.remove")}</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this payment method ending in{" "}
              {deleteDialog.method?.last4}?
              {deleteDialog.method?.isDefault && (
                <span className="block mt-2 text-yellow-600">
                  This is your default payment method. You'll need to set a new
                  default before removing it.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false })}
              size={isMobile ? "mobile" : "default"}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleteDialog.method?.id)}
              disabled={processingId || deleteDialog.method?.isDefault}
              size={isMobile ? "mobile" : "default"}
            >
              {processingId && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentMethods;
